import { useEffect, useRef } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { parseBlossomServerList } from '@/lib/appBlossom';

/**
 * NostrSync - Syncs user's Nostr data
 *
 * This component runs globally to sync various Nostr data when the user logs in.
 * Currently syncs:
 * - NIP-65 relay list (kind 10002)
 * - BUD-03 Blossom server list (kind 10063)
 *
 * Each sync runs exactly once per user login. The `updatedAt` guard inside the
 * callback prevents stale events from overwriting newer local data without
 * re-triggering the effect (which previously caused a double-sync loop that
 * cascaded `invalidateQueries` calls and aborted in-flight feed queries).
 */
export function NostrSync() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config, updateConfig } = useAppContext();

  // Use refs to read current config values inside the async callbacks without
  // including them in the dependency array (which caused the self-retrigger).
  const relayUpdatedAtRef = useRef(config.relayMetadata.updatedAt);
  const blossomUpdatedAtRef = useRef(config.blossomServerMetadata.updatedAt);
  useEffect(() => { relayUpdatedAtRef.current = config.relayMetadata.updatedAt; }, [config.relayMetadata.updatedAt]);
  useEffect(() => { blossomUpdatedAtRef.current = config.blossomServerMetadata.updatedAt; }, [config.blossomServerMetadata.updatedAt]);

  // Sync relays once per user login.
  useEffect(() => {
    if (!user) return;

    const syncRelaysFromNostr = async () => {
      try {
        const events = await nostr.query(
          [{ kinds: [10002], authors: [user.pubkey], limit: 1 }],
          { signal: AbortSignal.timeout(5000) }
        );

        if (events.length > 0) {
          const event = events[0];

          // Only update if the event is newer than our stored data.
          if (event.created_at > relayUpdatedAtRef.current) {
            const fetchedRelays = event.tags
              .filter(([name]) => name === 'r')
              .map(([_, url, marker]) => ({
                url,
                read: !marker || marker === 'read',
                write: !marker || marker === 'write',
              }));

            if (fetchedRelays.length > 0) {
              console.log('Syncing relay list from Nostr:', fetchedRelays);
              updateConfig((current) => ({
                ...current,
                relayMetadata: {
                  relays: fetchedRelays,
                  updatedAt: event.created_at,
                },
              }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to sync relays from Nostr:', error);
      }
    };

    syncRelaysFromNostr();
  }, [user, nostr, updateConfig]);

  // Sync Blossom servers once per user login.
  useEffect(() => {
    if (!user) return;

    const syncBlossomServersFromNostr = async () => {
      try {
        const events = await nostr.query(
          [{ kinds: [10063], authors: [user.pubkey], limit: 1 }],
          { signal: AbortSignal.timeout(5000) }
        );

        if (events.length > 0) {
          const event = events[0];

          if (event.created_at > blossomUpdatedAtRef.current) {
            const fetchedServers = parseBlossomServerList(event);

            if (fetchedServers.length > 0) {
              console.log('Syncing Blossom server list from Nostr:', fetchedServers);
              updateConfig((current) => ({
                ...current,
                blossomServerMetadata: {
                  servers: fetchedServers,
                  updatedAt: event.created_at,
                },
              }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to sync Blossom servers from Nostr:', error);
      }
    };

    syncBlossomServersFromNostr();
  }, [user, nostr, updateConfig]);

  return null;
}
